<template>
    <div
        class="card calendar-menu"
        v-if="openMenu"
        v-bind:style="{
            transform: currentY ? `translateY(${currentY}px)` : undefined,
            transition: currentY ? 'none' : undefined,
        }"
    >
        <div class="calendar-menu__grabber-container"
            v-on:touchstart="handleTouchStart"
            v-on:touchmove="handleTouchMove"
            v-on:touchend="handleTouchEnd"
        >
            <div class="calendar-menu__grabber"></div>
        </div>

        <div class="calendar-menu__presets">
            <button v-for="preset in presets"
                type="button"
                class="calendar-menu__preset"
                v-bind:class="{ 'calendar-menu__preset--active': isPresetActive(preset) }"
                v-bind:key="preset.key"
                v-on:click="applyPreset(preset)"
            >
                {{ $t(`calendar.${preset.key}`) }}
            </button>
        </div>

        <div class="calendar-picker">
            <div class="calendar-picker__header">
                <button type="button"
                    class="calendar-picker__title"
                    v-bind:class="{ 'calendar-picker__title--open': view === 'months' }"
                    v-on:click="toggleView"
                >
                    <span>{{ headerTitle }}</span>
                    <icon-chevron-down class="calendar-picker__title-icon" />
                </button>
                <div class="calendar-picker__nav">
                    <button type="button"
                        class="calendar-picker__nav-button"
                        v-bind:aria-label="$t('calendar.previous')"
                        v-on:click="step(-1)"
                    >
                        <icon-chevron-left />
                    </button>
                    <button type="button"
                        class="calendar-picker__nav-button"
                        v-bind:disabled="!canStepForward"
                        v-bind:aria-label="$t('calendar.next')"
                        v-on:click="step(1)"
                    >
                        <icon-chevron-right />
                    </button>
                </div>
            </div>

            <template v-if="view === 'days'">
                <div class="calendar-picker__weekdays">
                    <span v-for="weekday in weekdays" v-bind:key="weekday">{{ weekday }}</span>
                </div>
                <div class="calendar-picker__days"
                    v-bind:class="{ 'calendar-picker__days--preview': isPicking }"
                    v-on:mouseleave="hoverTime = null"
                >
                    <div v-for="(cell, index) in dayCells"
                        class="calendar-day"
                        v-bind:class="cell && dayClasses(cell)"
                        v-bind:key="cell ? cell.time : `empty-${index}`"
                    >
                        <button v-if="cell"
                            type="button"
                            class="calendar-day__button"
                            v-bind:disabled="cell.time > todayTime"
                            v-bind:aria-pressed="String(cell.time === rangeStart || cell.time === rangeEnd)"
                            v-on:click="pickDay(cell.time)"
                            v-on:mouseenter="hoverTime = cell.time"
                        >
                            {{ cell.day }}
                        </button>
                    </div>
                </div>
            </template>

            <div v-else class="calendar-picker__months">
                <button v-for="(month, index) in locale.monthsShortNominative"
                    type="button"
                    class="calendar-month"
                    v-bind:class="{
                        'calendar-month--current': viewYear === todayYear && index === todayMonth,
                        'calendar-month--viewed': index === viewMonth,
                    }"
                    v-bind:disabled="viewYear === todayYear && index > todayMonth"
                    v-bind:key="month"
                    v-on:click="openMonth(index)"
                >
                    {{ month }}
                </button>
            </div>
        </div>

        <div class="calendar-menu__footer">
            <span class="calendar-menu__summary"
                v-bind:class="{ 'calendar-menu__summary--hint': !selectionSummary }"
            >
                {{ selectionSummary || $t(isPicking ? 'calendar.pickEnd' : 'calendar.pickStart') }}
            </span>
            <button v-if="startTime !== null"
                type="button"
                class="calendar-menu__reset"
                v-on:click="reset"
            >
                {{ $t('common.reset') }}
            </button>
        </div>

        <button type="button" class="calendar-menu__close" v-on:click="toggleMenu">
            {{ $t('common.close') }}
        </button>
    </div>
</template>

<script>
import IconChevronDown from '@img/icons/lucide/chevron-down.svg?vue';
import IconChevronLeft from '@img/icons/lucide/chevron-left.svg?vue';
import IconChevronRight from '@img/icons/lucide/chevron-right.svg?vue';
import { mapState } from 'vuex';

const locales = {
    ru: {
        months: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
        // «18 мая», not «18 май»:
        monthsShort: ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
        monthsShortNominative: ['Янв', 'Фев', 'Март', 'Апр', 'Май', 'Июнь', 'Июль', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
        weekdaysMin: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        firstDayOfWeek: 1,
    },
    en: {
        months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        monthsShortNominative: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        weekdaysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
        firstDayOfWeek: 0,
    },
};

// Presets end today and include it: «7 days» is today and the six days before.
const PRESETS = [
    { key: 'today', days: 1 },
    { key: 'last7', days: 7 },
    { key: 'last30', days: 30 },
    { key: 'last90', days: 90 },
];

const startOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const shiftDays = (time, days) => {
    const date = new Date(time);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days).getTime();
};

export default {
    props: ['toggleMenu', 'openMenu'],
    data() {
        const today = startOfDay(new Date());
        // The filter outlives this component, so the picker starts from the stored range.
        // The store keeps [start, end) in unix seconds, end being the midnight after the last day:
        const [start, end] = this.$store.state.datePickerFilter;
        const startTime = start ? start * 1000 : null;
        const endTime = start ? startOfDay(new Date(end * 1000 - 1)).getTime() : null;
        const viewDate = new Date(endTime || today.getTime());

        return {
            startY: 0,
            currentY: 0,
            todayTime: today.getTime(),
            startTime,
            endTime,
            hoverTime: null,
            viewYear: viewDate.getFullYear(),
            viewMonth: viewDate.getMonth(),
            view: 'days',
            presets: PRESETS,
        };
    },
    computed: {
        locale() {
            return locales[this.appLocale] || locales.en;
        },
        todayYear() {
            return new Date(this.todayTime).getFullYear();
        },
        todayMonth() {
            return new Date(this.todayTime).getMonth();
        },
        headerTitle() {
            return this.view === 'days'
                ? `${this.locale.months[this.viewMonth]} ${this.viewYear}`
                : String(this.viewYear);
        },
        canStepForward() {
            return this.view === 'days'
                ? this.viewYear * 12 + this.viewMonth < this.todayYear * 12 + this.todayMonth
                : this.viewYear < this.todayYear;
        },
        weekdays() {
            const { weekdaysMin, firstDayOfWeek } = this.locale;
            return [...weekdaysMin.slice(firstDayOfWeek), ...weekdaysMin.slice(0, firstDayOfWeek)];
        },
        dayCells() {
            const offset = (new Date(this.viewYear, this.viewMonth, 1).getDay() - this.locale.firstDayOfWeek + 7) % 7;
            const count = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
            const cells = new Array(offset).fill(null);

            for (let day = 1; day <= count; day += 1) {
                const column = (offset + day - 1) % 7;
                cells.push({
                    day,
                    time: new Date(this.viewYear, this.viewMonth, day).getTime(),
                    // The range band gets rounded ends where a week row or the month breaks it:
                    roundLeft: column === 0 || day === 1,
                    roundRight: column === 6 || day === count,
                });
            }

            return cells;
        },
        isPicking() {
            return this.startTime !== null && this.endTime === null;
        },
        // While the second date is being chosen, the hovered day previews the range:
        rangeStart() {
            if (this.isPicking && this.hoverTime !== null) return Math.min(this.startTime, this.hoverTime);
            return this.startTime;
        },
        rangeEnd() {
            if (this.isPicking) return this.hoverTime !== null ? Math.max(this.startTime, this.hoverTime) : this.startTime;
            return this.endTime;
        },
        selectionSummary() {
            if (this.startTime === null || this.endTime === null) return null;
            return this.formatRange(this.startTime, this.endTime);
        },
        ...mapState(['appLocale', 'datePickerFilter']),
    },
    watch: {
        datePickerFilter([start]) {
            if (!start) {
                this.startTime = null;
                this.endTime = null;
            }
        },
    },
    methods: {
        dayClasses({ time, roundLeft, roundRight }) {
            const start = this.rangeStart;
            const end = this.rangeEnd;
            const isStart = time === start;
            const isEnd = time === end;
            const isRange = start !== null && start !== end;

            return {
                'calendar-day--today': time === this.todayTime,
                'calendar-day--selected': isStart || isEnd,
                'calendar-day--in-range': isRange && time > start && time < end,
                'calendar-day--range-start': isRange && isStart,
                'calendar-day--range-end': isRange && isEnd,
                'calendar-day--round-left': roundLeft,
                'calendar-day--round-right': roundRight,
            };
        },
        pickDay(time) {
            if (!this.isPicking) {
                this.startTime = time;
                this.endTime = null;
                return;
            }

            this.endTime = Math.max(this.startTime, time);
            this.startTime = Math.min(this.startTime, time);
            this.apply();
        },
        applyPreset({ days }) {
            this.endTime = this.todayTime;
            this.startTime = shiftDays(this.todayTime, 1 - days);
            this.viewYear = this.todayYear;
            this.viewMonth = this.todayMonth;
            this.apply();
        },
        isPresetActive({ days }) {
            return this.endTime === this.todayTime && this.startTime === shiftDays(this.todayTime, 1 - days);
        },
        apply() {
            this.hoverTime = null;
            this.$store.commit('setDatePickerFilter', [
                this.startTime / 1000,
                shiftDays(this.endTime, 1) / 1000,
            ]);
            this.toggleMenu();
        },
        reset() {
            this.startTime = null;
            this.endTime = null;
            this.hoverTime = null;
            if (this.datePickerFilter[0]) {
                this.$store.commit('resetDatePickerFilter');
            }
        },
        toggleView() {
            this.view = this.view === 'days' ? 'months' : 'days';
        },
        step(direction) {
            if (this.view === 'months') {
                this.viewYear += direction;
                return;
            }
            const date = new Date(this.viewYear, this.viewMonth + direction, 1);
            this.viewYear = date.getFullYear();
            this.viewMonth = date.getMonth();
        },
        openMonth(month) {
            this.viewMonth = month;
            this.view = 'days';
        },
        formatRange(startTime, endTime) {
            const { monthsShort } = this.locale;
            const start = new Date(startTime);
            const end = new Date(endTime);
            const endYear = end.getFullYear();
            const day = date => `${date.getDate()} ${monthsShort[date.getMonth()]}`;

            if (startTime === endTime) return `${day(end)} ${endYear}`;
            if (start.getFullYear() !== endYear) return `${day(start)} ${start.getFullYear()} – ${day(end)} ${endYear}`;
            if (start.getMonth() === end.getMonth()) return `${start.getDate()}–${day(end)} ${endYear}`;
            return `${day(start)} – ${day(end)} ${endYear}`;
        },
        handleTouchStart(event) {
            this.startY = event.touches[0].clientY;
            this.currentY = 0;
        },
        handleTouchMove(event) {
            this.currentY = Math.max(0, event.touches[0].clientY - this.startY);
        },
        handleTouchEnd() {
            if (this.currentY >= 140) {
                this.toggleMenu();
            }
            this.currentY = 0;
        },
    },
    components: {
        IconChevronDown,
        IconChevronLeft,
        IconChevronRight,
    },
};
</script>

<style lang="scss">
.calendar-menu {
    --calendar-cell-size: 40px;

    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 320px;
    box-sizing: border-box;
    padding: 12px;
    z-index: 90001;
    user-select: none;
    box-shadow: 0 1px 2px var(--card-box-shadow-color),
                0 16px 40px -12px rgba(0, 0, 0, .35);
    transform-origin: top right;
    animation: calendar-menu-in .16s cubic-bezier(.2, .8, .2, 1);

    &__grabber-container {
        display: none;
        justify-content: center;
        align-items: center;
        height: 20px;
        margin: -4px 0 4px;
        touch-action: none;
    }
    &__grabber {
        width: 36px;
        height: 5px;
        border-radius: 3px;
        background-color: var(--card-border-color);
    }

    &__presets {
        display: flex;
        gap: 6px;
        padding-bottom: 12px;
        margin-bottom: 8px;
        border-bottom: 1px solid var(--card-row-separator);
    }
    &__preset {
        flex: 1 1 0;
        height: 30px;
        padding: 0 6px;
        border: none;
        border-radius: 8px;
        background: var(--body-light-muted-color);
        color: var(--body-text-color);
        font: inherit;
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        cursor: pointer;
        transition: background-color .15s ease, color .15s ease;

        &:hover {
            background: var(--icon-button-hover-background);
        }
        &--active, &--active:hover {
            background: var(--blue-bright-background);
            color: var(--blue-bright);
        }
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        min-height: 32px;
        margin-top: 8px;
        padding: 8px 4px 0;
        border-top: 1px solid var(--card-row-separator);
    }
    &__summary {
        font-size: 13px;
        font-weight: 500;
        font-variant-numeric: tabular-nums;
        color: var(--body-text-color);

        &--hint {
            font-weight: 400;
            color: var(--body-muted-text-color);
        }
    }
    &__reset {
        flex-shrink: 0;
        height: 28px;
        padding: 0 10px;
        margin-right: -6px;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: var(--blue-bright);
        font: inherit;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;

        &:hover {
            background: var(--blue-bright-background);
        }
    }

    &__close {
        display: none;
        width: 100%;
        height: 44px;
        margin-top: 12px;
        border: none;
        border-radius: 12px;
        background: var(--body-light-muted-color);
        color: var(--body-text-color);
        font: inherit;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;

        &:active {
            opacity: .8;
        }
    }
}

.calendar-picker {
    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 4px;
    }
    &__title {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 32px;
        padding: 0 6px 0 8px;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: var(--body-text-color);
        font: inherit;
        font-size: 15px;
        font-weight: 600;
        letter-spacing: -0.01em;
        cursor: pointer;
        transition: background-color .15s ease;

        &:hover {
            background: var(--icon-button-hover-background);
        }
    }
    &__title-icon {
        width: 16px;
        height: 16px;
        color: var(--body-muted-text-color);
        transition: transform .2s ease;
    }
    &__title--open &__title-icon {
        transform: rotate(180deg);
    }
    &__nav {
        display: flex;
        gap: 2px;
    }
    &__nav-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: var(--body-text-color);
        cursor: pointer;
        transition: background-color .15s ease, opacity .15s ease;

        svg {
            width: 18px;
            height: 18px;
        }
        &:hover:not(:disabled) {
            background: var(--icon-button-hover-background);
        }
        &:disabled {
            opacity: .3;
            cursor: default;
        }
    }

    &__weekdays,
    &__days {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
    }
    &__weekdays {
        span {
            height: 28px;
            line-height: 28px;
            text-align: center;
            font-size: 12px;
            font-weight: 500;
            color: var(--body-muted-text-color);
        }
    }
    &__days {
        row-gap: 2px;
    }
    &__months {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
        padding-top: 4px;
    }
}

.calendar-day {
    position: relative;
    display: flex;
    justify-content: center;
    height: var(--calendar-cell-size);

    // The range band sits behind the day buttons and joins them into one strip:
    &::before {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background: var(--blue-bright-background);
        opacity: 0;
        transition: opacity .12s ease;
    }
    &--in-range::before,
    &--range-start::before,
    &--range-end::before {
        opacity: 1;
    }
    &--range-start::before {
        left: 50%;
    }
    &--range-end::before {
        right: 50%;
    }
    &--in-range.calendar-day--round-left::before {
        left: calc(50% - var(--calendar-cell-size) / 2);
        border-top-left-radius: 999px;
        border-bottom-left-radius: 999px;
    }
    &--in-range.calendar-day--round-right::before {
        right: calc(50% - var(--calendar-cell-size) / 2);
        border-top-right-radius: 999px;
        border-bottom-right-radius: 999px;
    }
    // A range that starts on the last day of a row (or ends on the first) has no band to draw:
    &--range-start.calendar-day--round-right::before,
    &--range-end.calendar-day--round-left::before {
        opacity: 0;
    }

    &__button {
        position: relative;
        width: var(--calendar-cell-size);
        height: var(--calendar-cell-size);
        padding: 0;
        border: none;
        border-radius: 50%;
        background: transparent;
        color: var(--body-text-color);
        font: inherit;
        font-size: 14px;
        font-variant-numeric: tabular-nums;
        cursor: pointer;
        transition: background-color .12s ease, color .12s ease;

        &:hover:not(:disabled) {
            background: var(--icon-button-hover-background);
        }
        &:disabled {
            opacity: .3;
            cursor: default;
        }
    }

    &--today &__button {
        color: var(--blue-bright);
        font-weight: 600;

        &::after {
            content: "";
            position: absolute;
            left: 50%;
            bottom: 6px;
            width: 4px;
            height: 4px;
            margin-left: -2px;
            border-radius: 50%;
            background: currentColor;
        }
    }

    &--selected &__button,
    &--selected &__button:hover:not(:disabled) {
        background: var(--blue-bright);
        color: #fff;
        font-weight: 600;
    }
}

// Hover preview of a range that isn't picked yet is a paler version of the real one:
.calendar-picker__days--preview .calendar-day::before {
    background: var(--body-light-muted-color);
}

.calendar-month {
    height: 40px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: var(--body-text-color);
    font: inherit;
    font-size: 14px;
    cursor: pointer;
    transition: background-color .12s ease;

    &:hover:not(:disabled) {
        background: var(--icon-button-hover-background);
    }
    &:disabled {
        opacity: .3;
        cursor: default;
    }
    &--current {
        color: var(--blue-bright);
        font-weight: 600;
    }
    &--viewed, &--viewed:hover:not(:disabled) {
        background: var(--blue-bright-background);
        color: var(--blue-bright);
        font-weight: 600;
    }
}

@keyframes calendar-menu-in {
    from {
        opacity: 0;
        transform: translateY(-4px) scale(.98);
    }
}

@keyframes calendar-sheet-in {
    from {
        transform: translateY(calc(100% + 8px + env(safe-area-inset-bottom)));
    }
}

@media screen and (max-width: 599px) {
    .calendar-menu {
        --calendar-cell-size: 44px;

        // A floating sheet: inset from the screen edges and rounded on all corners.
        position: fixed;
        top: auto;
        bottom: calc(8px + env(safe-area-inset-bottom));
        left: 8px;
        right: 8px;
        width: auto;
        padding: 12px 16px 16px;
        border-radius: 32px !important;
        transition: transform .3s cubic-bezier(.2, .8, .2, 1);
        animation: calendar-sheet-in .3s cubic-bezier(.2, .8, .2, 1);

        &__grabber-container {
            display: flex;
        }
        &__preset {
            height: 34px;
            border-radius: 10px;
            font-size: 14px;
        }
        &__close {
            display: block;
            border-radius: 16px;
        }
    }
    .calendar-picker__title {
        font-size: 17px;
    }
    .calendar-day__button {
        font-size: 16px;
    }
    .calendar-month {
        height: 48px;
        font-size: 15px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .calendar-menu {
        animation: none;
    }
}
</style>
